import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Phone, Mail, ChevronDown, Car, Plane, Compass, Users, Shield, Play, ArrowRight, X, Volume2, VolumeX } from 'lucide-react';
import BookingModal from '../components/BookingModal';
import '../styles/Home.css';

// ─── Assets ───
const LOGO = "https://customer-assets.emergentagent.com/job_assist-vip-luxury/artifacts/9et8d8v2_A_logo_for_ASSIST_VIP_Luxury_Services_features_a-removebg-preview.png";
const IS_MOBILE = typeof window !== "undefined" && window.matchMedia("(max-width: 768px)").matches;
const HERO_VIDEO = IS_MOBILE ? "/videos/hero_kanye_mobile.mp4" : "/videos/hero_kanye_desktop.mp4";
const HERO_POSTER = IS_MOBILE ? "/videos/hero_kanye_poster_m.jpg" : "/videos/hero_kanye_poster.jpg";

const FLEET = [
  { name: 'Mercedes V-Class VIP', type: 'Luxury Van', image: 'https://customer-assets.emergentagent.com/job_assist-vip-luxury/artifacts/q7pjaxik_9ba36012-f21f-4067-a257-c9e9c912ce1d.JPEG' },
  { name: 'Mercedes S-Class', type: 'Executive Sedan', image: 'https://customer-assets.emergentagent.com/job_assist-vip-luxury/artifacts/dkixx4rj_1fe4b4c2-7cbf-4783-b18a-195ad2f15204.jpg' },
  { name: 'Mercedes Sprinter VIP', type: 'Executive Shuttle', image: 'https://customer-assets.emergentagent.com/job_637ba2ed-95b8-44cc-9608-40a8ba195675/artifacts/de1o1smj_WhatsApp%20Image%202026-03-09%20at%2000.09.34%20%282%29.jpeg' },
];

const EDITORIAL_VIDEOS = [
  { name: 'Kanye West', desc: 'VIP Service · Istanbul', poster: '/videos/kanye_poster.jpg', video: '/videos/kanye_moment.mp4', layout: 'large' },
  { name: 'Jason Derulo', desc: 'Private Transfer · Istanbul', poster: '/videos/jason_poster.jpg', video: '/videos/jason_moment.mp4', layout: 'large' },
  { name: 'French Montana', desc: 'VIP Service · Istanbul', poster: '/videos/french_poster.jpg', video: '/videos/french_moment.mp4', layout: 'medium' },
  { name: 'Tiësto', desc: 'VIP Transfer · Istanbul', poster: '/videos/tiesto_poster.jpg', video: '/videos/tiesto_moment.mp4', layout: 'medium' },
  { name: 'Operations', desc: 'Behind the Scenes', poster: '/videos/mix_poster.jpg', video: '/videos/mix_moment.mp4', layout: 'wide' },
];

const PHOTO_MOMENTS = [
  { name: 'Russ Millions', desc: 'International Artist', image: 'https://customer-assets-gfyr7b9c.emergentagent.net/job_8cd71329-c0fa-4c62-b5e0-52abb905021e/artifacts/231e1fcf03c500e9_russ%20millions.jpeg' },
  { name: 'Draymond Green', desc: 'NBA · Golden State Warriors', image: 'https://customer-assets.emergentagent.com/job_637ba2ed-95b8-44cc-9608-40a8ba195675/artifacts/y3stxmon_00F0C1F4-B004-4CF8-B957-E9D92DC6B0D6.jpeg' },
  { name: 'Close Protection', desc: 'Executive Security', image: 'https://customer-assets-gfyr7b9c.emergentagent.net/job_8cd71329-c0fa-4c62-b5e0-52abb905021e/artifacts/3ad20fc466acdfe0_image.png' },
  { name: 'Burna Boy', desc: 'International Artist', image: 'https://customer-assets.emergentagent.com/job_637ba2ed-95b8-44cc-9608-40a8ba195675/artifacts/eh6yy3b9_IMG_5140.jpeg' },
  { name: 'Mostafa Mohamed', desc: 'Professional Footballer', image: 'https://customer-assets.emergentagent.com/job_637ba2ed-95b8-44cc-9608-40a8ba195675/artifacts/z9ndhf0k_IMG_5141.jpeg' },
];

const SERVICES = [
  { id: 'chauffeur', icon: Car, name: 'Private Chauffeur', desc: 'Hourly, daily and multi-day private chauffeur service with professional drivers across Istanbul and Turkey.' },
  { id: 'airport', icon: Plane, name: 'Airport Transfers', desc: 'Private meet & greet at arrivals, flight tracking and seamless transfer between airport and your destination.' },
  { id: 'concierge', icon: Compass, name: 'Concierge & Experiences', desc: 'Restaurant reservations, shopping assistance, yacht charters and bespoke experiences tailored to your visit.' },
  { id: 'groups', icon: Users, name: 'Groups & Delegations', desc: 'Coordinated transportation for corporate teams, events, delegations and group travel across Istanbul.' },
  { id: 'protection', icon: Shield, name: 'Close Protection', desc: 'Discreet personal security services arranged on request for high-profile guests visiting Turkey.' },
];

/* ─── Inline Video Card with IntersectionObserver ─── */
const EditorialVideoCard = ({ item, onWatchFilm }) => {
  const videoRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        const vid = videoRef.current;
        if (!vid) return;
        if (entry.isIntersecting && document.body.style.overflow !== 'hidden') {
          vid.play().catch(() => {});
        } else {
          vid.pause();
        }
      },
      { threshold: 0.3 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const isVertical = item.layout === 'large' || item.layout === 'medium';

  return (
    <div
      ref={containerRef}
      className={`editorial-card editorial-${item.layout}`}
      data-testid={`editorial-video-${item.name.toLowerCase().replace(/\s/g, '-')}`}
    >
      <div className={`editorial-media ${isVertical ? 'vertical' : 'horizontal'}`}>
        <img src={item.poster} alt={item.name} className="editorial-poster" loading="lazy" />
        <video
          ref={videoRef}
          muted
          loop
          playsInline
          preload="none"
          poster={item.poster}
          className="editorial-video"
        >
          <source src={item.video} type="video/mp4" />
        </video>
        <div className="editorial-overlay" />
      </div>

      <div className="editorial-info">
        <span className="editorial-name">{item.name}</span>
        <span className="editorial-desc">{item.desc}</span>
      </div>

      <button className="editorial-watch" onClick={() => onWatchFilm(item.video)} data-testid={`watch-film-${item.name.toLowerCase().replace(/\s/g, '-')}`}>
        <Play size={14} fill="currentColor" /> Watch Film
      </button>
    </div>
  );
};

const Home = () => {
  const [scrolled, setScrolled] = useState(false);
  const [heroVideoLoaded, setHeroVideoLoaded] = useState(false);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [preService, setPreService] = useState('');
  const [preVehicle, setPreVehicle] = useState('');
  const [activeVideo, setActiveVideo] = useState(null);
  const modalVideoRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll);

    const observer = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('visible'); }),
      { threshold: 0.1 }
    );
    document.querySelectorAll('.fade-in').forEach(el => observer.observe(el));

    // Elfsight (deferred)
    const t = setTimeout(() => {
      if (!document.querySelector('script[src*="elfsight"]')) {
        const s = document.createElement('script');
        s.src = 'https://elfsightcdn.com/platform.js';
        s.async = true;
        document.body.appendChild(s);
      }
    }, 4000);

    return () => { window.removeEventListener('scroll', onScroll); observer.disconnect(); clearTimeout(t); };
  }, []);

  // ─── Tracking helpers (separate events, no double counting) ───
  const trackWhatsAppClick = useCallback((label = 'general') => {
    if (typeof window.gtag !== 'function') return;
    window.gtag('event', 'whatsapp_click', { event_category: 'engagement', event_label: label });
  }, []);

  const trackQuoteRequest = useCallback((service = '') => {
    if (typeof window.gtag !== 'function') return;
    window.gtag('event', 'conversion', { 'send_to': 'AW-17988005381/1jDyCIC78IQcEIXcrIFD' });
    window.gtag('event', 'quote_request', { event_category: 'conversion', event_label: service });
  }, []);

  const openWhatsApp = useCallback(() => {
    trackWhatsAppClick('direct');
    const msg = encodeURIComponent("Hello, I would like to learn about ASSIST VIP services in Istanbul.");
    window.open(`https://wa.me/905447153414?text=${msg}`, '_blank');
  }, [trackWhatsAppClick]);

  const openBooking = useCallback((service = '', vehicle = '') => {
    setPreService(service);
    setPreVehicle(vehicle);
    setBookingOpen(true);
  }, []);

  const openVideoModal = useCallback((src) => {
    document.querySelectorAll('.editorial-video').forEach(v => v.pause());
    setActiveVideo(src);
    document.body.style.overflow = 'hidden';
  }, []);

  const closeVideoModal = useCallback(() => {
    setActiveVideo(null);
    document.body.style.overflow = '';
    document.querySelectorAll('.editorial-video').forEach(v => {
      const r = v.getBoundingClientRect();
      if (r.bottom > 0 && r.top < window.innerHeight) v.play().catch(() => {});
    });
  }, []);

  return (
    <div className="luxury-site" data-testid="luxury-site">

      {/* ─── Header ─── */}
      <header className={`site-header ${scrolled ? 'scrolled' : ''}`} data-testid="site-header">
        <div className="header-inner">
          <a href="#hero" className="header-logo" data-testid="nav-logo">
            <img src={LOGO} alt="ASSIST VIP" className="notranslate" translate="no" />
          </a>
          <nav className="nav-links" data-testid="nav-links">
            <a href="#services">Services</a>
            <a href="#fleet">Fleet</a>
            <a href="#moments">Moments</a>
            <a href="#contact">Contact</a>
          </nav>
          <div className="header-right">
            <button onClick={openWhatsApp} className="header-cta" data-testid="header-whatsapp-btn">WhatsApp Concierge</button>
          </div>
        </div>
      </header>

      {/* ─── Hero ─── */}
      <section id="hero" className="hero" data-testid="hero-section">
        <div className="hero-media">
          <img src={HERO_POSTER} alt="" className={`hero-poster ${heroVideoLoaded ? 'hidden' : ''}`} />
          <video
            autoPlay loop muted playsInline
            preload="auto"
            className={`hero-video ${heroVideoLoaded ? 'loaded' : ''}`}
            onCanPlay={() => setHeroVideoLoaded(true)}
          >
            <source src={HERO_VIDEO} type="video/mp4" />
          </video>
          <div className="hero-overlay" />
        </div>
        <div className="hero-content">
          <h1 className="hero-title notranslate" translate="no">
            Your Time. Your Privacy.<br />Every Detail.
          </h1>
          <div className="hero-actions" data-testid="hero-buttons">
            <button onClick={() => openBooking()} className="btn-champagne" data-testid="hero-plan-stay-button">Plan Your Stay</button>
            <button onClick={openWhatsApp} className="btn-outline" data-testid="hero-whatsapp-button">WhatsApp Concierge</button>
          </div>
        </div>
        <div className="scroll-indicator"><ChevronDown size={20} /></div>
      </section>

      {/* ─── Heritage Bar ─── */}
      <section className="heritage-bar" data-testid="heritage-bar">
        <div className="heritage-inner fade-in">
          <div><div className="heritage-stat-value notranslate" translate="no">2016</div><div className="heritage-stat-label">Founded in Istanbul</div></div>
          <div><div className="heritage-stat-value">24/7</div><div className="heritage-stat-label">Availability</div></div>
          <div><div className="heritage-stat-value">100%</div><div className="heritage-stat-label">Privacy Record</div></div>
          <div><div className="heritage-stat-value">Global</div><div className="heritage-stat-label">Elite Clientele</div></div>
        </div>
      </section>

      {/* ─── Selected Moments — Editorial Video Gallery ─── */}
      <section id="moments" className="moments-section" data-testid="moments-section">
        <div className="section-inner">
          <p className="section-label fade-in">Heritage</p>
          <h2 className="section-heading fade-in">Selected Moments</h2>

          <div className="editorial-grid fade-in">
            {EDITORIAL_VIDEOS.map((item, i) => (
              <EditorialVideoCard key={i} item={item} onWatchFilm={openVideoModal} />
            ))}
          </div>

          {/* Photo strip */}
          <div className="photo-strip fade-in">
            {PHOTO_MOMENTS.map((m, i) => (
              <div key={i} className="photo-strip-card" data-testid={`moment-card-${i}`}>
                <img src={m.image} alt={m.name} loading="lazy" />
                <div className="photo-strip-overlay">
                  <span className="photo-strip-name">{m.name}</span>
                </div>
              </div>
            ))}
          </div>

          <p className="moment-disclaimer fade-in">
            Images and videos shown with permission. ASSIST VIP provides transportation and logistics services only.
          </p>
        </div>
      </section>

      {/* ─── Services (calm, spacious) ─── */}
      <section id="services" className="services-section" data-testid="services-section">
        <div className="section-inner">
          <p className="section-label fade-in">What We Do</p>
          <h2 className="section-heading fade-in">Our Services</h2>
          <p className="section-desc fade-in">
            From private chauffeur to close protection — every service tailored to your schedule, privacy and comfort.
          </p>
          <div className="services-grid fade-in">
            {SERVICES.map(s => {
              const Icon = s.icon;
              return (
                <div key={s.id} className="service-card" data-testid={`service-card-${s.id}`}>
                  <Icon className="service-icon" />
                  <h3>{s.name}</h3>
                  <p>{s.desc}</p>
                  <button className="service-cta" onClick={() => openBooking(s.id)} data-testid={`service-cta-${s.id}`}>
                    Request Quote <ArrowRight size={14} />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── Fleet (calm, spacious) ─── */}
      <section id="fleet" className="fleet-section" data-testid="fleet-section">
        <div className="section-inner">
          <p className="section-label fade-in">Executive Fleet</p>
          <h2 className="section-heading fade-in">Our Vehicles</h2>
          <p className="section-desc fade-in">
            Real vehicles from our fleet. Every car professionally maintained and operated by experienced chauffeurs.
          </p>
          <div className="fleet-grid fade-in">
            {FLEET.map((v, i) => (
              <div key={i} className="fleet-card" data-testid={`fleet-card-${i}`}>
                <div className="fleet-card-image"><img src={v.image} alt={v.name} loading="lazy" /></div>
                <div className="fleet-card-body">
                  <div className="fleet-card-name">{v.name}</div>
                  <div className="fleet-card-type">{v.type}</div>
                  <button className="fleet-request-btn" onClick={() => openBooking('', v.name)} data-testid={`fleet-request-btn-${i}`}>
                    Request This Vehicle
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── How It Works ─── */}
      <section className="how-section" data-testid="how-section">
        <div className="section-inner">
          <p className="section-label fade-in">Process</p>
          <h2 className="section-heading fade-in">How It Works</h2>
          <div className="how-steps fade-in">
            {[
              { num: '01', title: 'Request', desc: 'Share your travel dates, service needs and preferences via our form or WhatsApp.' },
              { num: '02', title: 'Quote', desc: 'We check availability and send you a detailed proposal within minutes.' },
              { num: '03', title: 'Confirm', desc: 'Approve the quote and complete payment to secure your booking.' },
              { num: '04', title: 'Arrive', desc: 'Your chauffeur meets you at the agreed time and place. Enjoy Istanbul.' },
            ].map((s, i) => (
              <div key={i} className="how-step" data-testid={`how-step-${i}`}>
                <div className="step-num">{s.num}</div>
                <h3>{s.title}</h3>
                <p>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Instagram Feed ─── */}
      <section className="instagram-section" data-testid="instagram-section">
        <div className="section-inner">
          <p className="section-label fade-in">Follow Us</p>
          <h2 className="section-heading fade-in">Instagram</h2>
          <p className="section-desc fade-in">@assistvip</p>
          <div className="fade-in">
            <div className="elfsight-app-b94f1e59-1f60-4d2d-a498-c67a77cf09a5" data-elfsight-app-lazy />
          </div>
        </div>
      </section>

      {/* ─── CTA ─── */}
      <section id="contact" className="cta-section" data-testid="cta-section">
        <div className="cta-inner fade-in">
          <p className="section-label">Ready?</p>
          <h2 className="cta-title">Plan Your Istanbul Experience</h2>
          <p className="cta-text">Tell us your dates, preferences and needs. Our concierge team will arrange everything — from airport to departure.</p>
          <div className="hero-actions">
            <button onClick={() => openBooking()} className="btn-champagne" data-testid="cta-plan-stay-button">Plan Your Stay</button>
            <button onClick={openWhatsApp} className="btn-outline" data-testid="cta-whatsapp-button">WhatsApp Concierge</button>
          </div>
        </div>
      </section>

      {/* ─── Footer ─── */}
      <footer className="site-footer" data-testid="site-footer">
        <div className="footer-inner">
          <img src={LOGO} alt="ASSIST VIP" className="footer-logo-img notranslate" translate="no" />
          <p className="footer-tagline">Discreet · Reliable · Elite</p>
          <div className="footer-contact-row">
            <a href="tel:+905447153414"><Phone size={18} /> +90 544 715 34 14</a>
            <a href="mailto:info@assistvipgroup.com"><Mail size={18} /> info@assistvipgroup.com</a>
          </div>
          <p className="footer-seo-text">
            <span className="notranslate" translate="no">ASSIST VIP</span> provides luxury chauffeur service, VIP airport transfers and private driver services in Istanbul.
            Our executive fleet and concierge team serve international travelers, athletes, artists and business executives visiting Turkey.
          </p>
          <div className="footer-bottom">
            <p>&copy; {new Date().getFullYear()} <span className="notranslate" translate="no">ASSIST VIP</span> Luxury Services. Istanbul, Turkey.</p>
          </div>
        </div>
      </footer>

      {/* ─── Floating WhatsApp ─── */}
      <button className="whatsapp-float" onClick={openWhatsApp} data-testid="floating-whatsapp-button" aria-label="WhatsApp">
        <span className="whatsapp-pulse" />
        <svg viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.981.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
      </button>

      {/* ─── Elfsight AI Chat Widget ─── */}
      <div className="elfsight-app-6a47eb8d-34db-4294-b498-09e27a341bf0" data-elfsight-app-lazy />

      {/* ─── Video Modal ─── */}
      {activeVideo && (
        <div className="video-overlay" data-testid="video-overlay" onClick={e => e.target === e.currentTarget && closeVideoModal()}>
          <div className="video-modal-content">
            <button className="video-close" onClick={closeVideoModal} data-testid="video-close"><X size={24} /></button>
            <video ref={modalVideoRef} autoPlay playsInline controls style={{ width: '100%', maxHeight: '90vh', objectFit: 'contain', background: '#000' }}>
              <source src={activeVideo} type="video/mp4" />
            </video>
          </div>
        </div>
      )}

      {/* ─── Booking Modal ─── */}
      <BookingModal
        isOpen={bookingOpen}
        onClose={() => setBookingOpen(false)}
        preselectedService={preService}
        preselectedVehicle={preVehicle}
        onTrackQuote={trackQuoteRequest}
      />
    </div>
  );
};

export default Home;
